import React, {useEffect, useState} from 'react';
import { useAppDispatch, useAppSelector } from '../../../../Config/Hooks';
import { getEntities, reset } from './PowerStationElementsReducer';
import type { RootState } from '../../../../Config/Store';
import {mapElementToNode} from "../../../../Shared/Helpers/mapElementToNode";
import {ACard, Flow, Skeleton} from "@atiautomacao/ati-ui-library";
import { useSkidNavigation } from '../../../../Shared/Hooks/useSkidNavigation';
import { useEquipmentNavigation } from '../../../../Shared/Hooks/useEquipmentNavigation';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import type {Element} from '../../../../Shared/Types/element';
import useInterval from "../../../../Shared/Hooks/useInterval";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {CommandDTO} from "../../../../Shared/Components/ModalForSendingTelecommand";
import {SendingTelecommandUnifilar} from "../../../../Shared/Components/SendingTelecommandUnifilar";
import {useSnackbar} from "notistack";
import {findAllCommands} from "../../../../Utils/CommandService";

interface Props {
    powerStationId: number;
}

const PowerStationElements = ({ powerStationId }: Props): JSX.Element => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigateToSkid = useSkidNavigation();
    const navigateToEquipment = useEquipmentNavigation();
    const { entities, isLoading } = useAppSelector((state: RootState) => state.powerStationElements);
    const [elementsData, setElementsData] = useState<any[]>([])
    const [equipmentId, setEquipmentId] = useState<number>(-1);
    const [teleCommandList, setTeleCommandList] = useState<Array<CommandDTO>>([])
    const [open, setOpen] = useState(false)
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    

    useEffect(() => {
        dispatch(reset())
        dispatch(getEntities(powerStationId));
    }, [powerStationId]);

    useInterval(() => {
        dispatch(reset())
        dispatch(getEntities(powerStationId));
    }, userConfig.mainUpdateInterval); // 5 minutes

    useEffect(() => {
        if(entities && entities.elements.length > 0) {
            let nodesElements = [...entities.elements];
            nodesElements.forEach((elt: any) => {
                if(elt.parentId !== null && !nodesElements.find((elm: any) => elm.id === elt.parentId)) {
                    nodesElements.push({
                        id: elt.parentId,
                        label: elt.parentId.toUpperCase(),
                        edges: [],
                        leftValues: [],
                        rightValues: [],
                        topValues: [],
                        icon: '',
                        isNavigable: true,
                        status: '',
                        skidName: '',
                        type: 'group',
                        idImage: undefined
                    })
                }
            });

            setElementsData(nodesElements);
        } else {
            setElementsData([]);
        }
    }, [entities]);

    const handleNodeClick = (id: string, isNavigable: boolean, label: string): void => {
        const newID = id?.replace(/\D/g, "")
        let node = entities?.elements.find(element => element.id === id);
        if (node?.isNavigable) {
            let idNumber = Number(newID);
            if(id.toLowerCase().startsWith('s')){
                navigateToSkid(powerStationId, idNumber);
                return
            }
            navigateToEquipment(idNumber);
        }
    }

    const sortNodeChildren = (node: Element): Element => {
        const sortedEdges = node.edges.map(edge => sortNodeChildren(edge));
        sortedEdges.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
        return { ...node, edges: sortedEdges };
    };

    const compareElements = (a: Element, b: Element): number => {
        const hasSkidA = !!(a.skidName && a.skidName.trim() !== '');
        const hasSkidB = !!(b.skidName && b.skidName.trim() !== '');

        if (hasSkidA !== hasSkidB) {
            return hasSkidA ? -1 : 1;
        }

        if (hasSkidA && hasSkidB && a.skidName !== b.skidName) {
            return a.skidName.localeCompare(b.skidName, undefined, { numeric: true });
        }

        return a.label.localeCompare(b.label, undefined, { numeric: true });
    };

    const sortTree = (elements: Element[]): Element[] => {
        return elements.map(node => ({
            ...node,
            edges: node.edges ? sortTree(node.edges) : []
        })).sort(compareElements);
    };

    const findSkid = (element: Element): string => {
        if (element.label.toLowerCase().includes('unidade geradora') || element.label.toLowerCase().includes('skid')) {
            return element.label;
        }
        if (element.edges) {
            for (const edge of element.edges) {
                const result = findSkid(edge);
                if (result) return result;
            }
        }
        return element.label;
    };

    const handleCloseModal = () => {
        setOpen(false);
        setTeleCommandList([])
        setMenuPosition(null);
        setEquipmentId(-1)
    };

    const [menuPosition, setMenuPosition] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);

    const handleNodeContextMenu = (event: React.MouseEvent, node: any) => {
        event.preventDefault();
        if(node.id != null){
            if(node.id.startsWith('e')){
                setMenuPosition({
                    mouseX: event.clientX - 2,
                    mouseY: event.clientY - 4,
                });
                const id = node.id.substring(1);
                setEquipmentId(Number(id));
            }
        }else{
            setMenuPosition(null);
            setEquipmentId(-1)
        }

    };

    const handleMenuContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setMenuPosition(null);
        setEquipmentId(-1)

    };
    const toOpen = () => {
        if(teleCommandList.length > 0){
            setOpen(true)
        }else{
            enqueueSnackbar("Comandos não encontrados", {variant: "error"});
            setOpen(false)
            setMenuPosition(null);
            setEquipmentId(-1)
        }

    }
    const handleClose = () => {
        setMenuPosition(null);
        setEquipmentId(-1)
    };

    useEffect(() => {
        if (equipmentId !== -1) {
            findAllCommands(equipmentId).then(
                (response:any) => {
                    if (response.status === 204) {
                        setTeleCommandList([]);
                    } else {
                        setTeleCommandList(response.data.data);
                    }
                }
            ).catch((error:any) => {
                setTeleCommandList([]);
                console.error("Error fetching tele commands:", error);
            })
        }else{
            setTeleCommandList([]);
        }
    }, [equipmentId]);

    let cardContentToRender: JSX.Element;
    if (isLoading) {
        cardContentToRender = <Skeleton animation="wave" height={500} variant="rectangular" width={800} />
    } else if (entities && elementsData && elementsData.length > 0) {
        const newEntities = sortTree([...elementsData])
        cardContentToRender = (
            <div aria-label={"dashboard-power-station-elements-flow"} style={{height: 500, width: '100%'}}>
                    <Flow
                        nodes={newEntities.map(element => mapElementToNode(element, entities.images))}
                        onNodeClick={handleNodeClick} isCreating={false}
                        onContextMenu={handleNodeContextMenu}

                    />
                    <Menu
                        open={menuPosition !== null}
                        onClose={handleClose}
                        anchorReference="anchorPosition"
                        disablePortal
                        onContextMenu={handleMenuContextMenu}
                        anchorPosition={
                            menuPosition !== null
                                ? {top: menuPosition.mouseY, left: menuPosition.mouseX}
                                : undefined
                        }
                    >
                        <MenuItem onClick={toOpen}>Enviar comando</MenuItem>
                    </Menu>
            </div>
        );
    } else {
        cardContentToRender = (
            <Container fixed>
                <Box sx={{
                    bgcolor: '#e1e0e0', height: '500px', width: '800px', alignItems: 'center',
                    justifyContent: 'center', display: 'flex'
                }}>
                    <div style={{textAlign: 'center', fontSize: '23px', color: '#646363'}}>
                        <span>Nenhum equipamento encontrado</span>
                    </div>
                </Box>

            </Container>
        );
    }

    return (
        <>
            <ACard
                styleProps={{
                    cardStyle: {width:'100%'}
                }}
                title="Unifilar"
                // headerActions={<HeaderActions/>}
                // footerActions={<FooterActions/>}
            >
                {cardContentToRender}
            </ACard>
            {teleCommandList != null && teleCommandList.length > 0 && <SendingTelecommandUnifilar commandDTOList={teleCommandList} open={open} onCloseModal={handleCloseModal}/>}
        </>
    );
};

export default PowerStationElements;
